import type {CSS} from '@pinecast/styles';
import styled, {focusVisibleRing} from '@pinecast/styles';
import * as React from 'react';

import {DEFAULT_FONT} from './constants';
import DownChevronMedium from './icons/DownChevronMedium';
import {useLabelledControl} from './LabelContext';

const Wrapper = styled(
  'div',
  ({$disabled}: {$disabled: boolean}, designColors) => ({
    alignItems: 'center',
    backgroundColor: designColors.contentBackground,
    borderRadius: 4,
    color: designColors.contentTextAccent,
    display: 'inline-flex',
    fontSize: 14,
    height: 32,
    maxWidth: '100%',
    opacity: 1,
    position: 'relative',
    transition: 'box-shadow 0.2s, opacity 0.2s',
  }),
);
const Select_ = styled(
  'select',
  ({disabled}: {disabled: boolean}, designColors) => ({
    appearance: 'none',
    MozAppearance: 'none',
    WebkitAppearance: 'none',
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: designColors.borderGrayNormal,
    boxShadow: `inset 0 0 0 3px transparent, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
    borderRadius: 4,
    color: designColors.contentTextAccent,
    flexGrow: 1,
    fontFamily: DEFAULT_FONT,
    fontSize: 14,
    height: 32,
    lineHeight: 32,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    maxWidth: '100%',
    padding: '0 25px 0 10px',
    transition: 'border-color 0.2s, box-shadow 0.2s, opacity 0.2s',
    width: 'auto',

    ':hover': !disabled
      ? {
          // borderColor: designColors.inputBorder,
          boxShadow: `inset 0 0 0 3px ${designColors.borderBlueLight}, 0 2px 0 1px rgba(0, 0, 0, 0.06), 0 1px 1px rgba(0, 0, 0, 0.12)`,
        }
      : undefined,
    ':disabled': {
      opacity: 0.5,
    },

    ...focusVisibleRing(designColors),
  }),
  {className: 'Select'},
);

export type Option = {
  key: string | number;
  label: string;
  /**
   * The language of `label`, when it is not the language of the page: an option
   * holds text only, so it cannot be marked from within (WCAG 3.1.2).
   */
  lang?: string;
};

const Select = ({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  autoFocus,
  disabled,
  id,
  invalid,
  nativeEvents,
  onChange,
  options,
  style,
  tabIndex,
  value,
}: {
  'aria-describedby'?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  id?: string;
  /** Marks the field invalid. A surrounding `<Label>` with an error does too. */
  invalid?: boolean;
  nativeEvents?: Partial<{
    onBlur: React.HTMLAttributes<HTMLSelectElement>['onBlur'];
    onChange: React.HTMLAttributes<HTMLSelectElement>['onChange'];
    onClick: React.HTMLAttributes<HTMLSelectElement>['onClick'];
    onFocus: React.HTMLAttributes<HTMLSelectElement>['onFocus'];
    onKeyDown: React.HTMLAttributes<HTMLSelectElement>['onKeyDown'];
    onKeyUp: React.HTMLAttributes<HTMLSelectElement>['onKeyUp'];
    onKeyPress: React.HTMLAttributes<HTMLSelectElement>['onKeyPress'];
  }>;
  onChange: (newValue: string) => void;
  options: Array<Option>;
  style?: CSS;
  tabIndex?: number;
  value: string | number;
}) => {
  const labelling = useLabelledControl({
    'aria-describedby': ariaDescribedBy,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    id,
    invalid,
  });
  return (
    <Wrapper style={style} $disabled={disabled || false}>
      <Select_
        autoFocus={autoFocus}
        disabled={disabled || false}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          onChange(e.target.value)
        }
        {...nativeEvents}
        tabIndex={tabIndex}
        value={value}
        {...labelling}
      >
        {options.map(({key, label, lang}) => (
          <option key={String(key)} lang={lang} value={key}>
            {label}
          </option>
        ))}
      </Select_>
      <DownChevronMedium
        size={10}
        style={{
          position: 'absolute',
          right: 8,
          top: 0,
          bottom: 0,
          margin: 'auto',
          // The icon is on top of the native `<select>`. Without this, a click
          // on it lands on the icon and does not open the list.
          pointerEvents: 'none',
        }}
        aria-hidden="true"
      />
    </Wrapper>
  );
};

export default Select;
